import * as React from 'react';

import type { AudienceFilterSpec } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { getUserFilterPreset } from 'bundles/enterprise-admin-user-filters/constants';
import { useCreateAdminUserFilterMutation } from 'bundles/enterprise-admin-user-filters/hooks';
import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';
import {
  buildAssignedContentFilter,
  mapUserFilterFromClientToServerModel,
} from 'bundles/enterprise-admin-user-filters/utils';

/**
 * Translate the semantic audience spec into the client-side `UserFilter` the
 * filter mappers understand. Program-scoped funnel reminders always apply the
 * triggering program as a hard scope (`programIdSelection`); org-wide home
 * audiences reuse the canonical `getUserFilterPreset` filters — the exact same
 * single-membership filters the legacy preset messages target.
 */
export const buildClientUserFilter = (spec: AudienceFilterSpec): UserFilter => {
  switch (spec.audienceType) {
    case 'INVITED_NOT_JOINED':
      return {
        primaryFilter: {
          type: 'INVITED',
          programIdSelection: { values: [spec.programId] },
        },
      };
    case 'JOINED_NOT_ENROLLED':
      return {
        primaryFilter: {
          type: 'MEMBER',
          programIdSelection: { values: [spec.programId] },
          activityFilter: { type: 'NO_ENROLLMENTS' },
        },
      };
    case 'ORG_INVITED_NOT_JOINED':
      return getUserFilterPreset('invited').filter;
    case 'ORG_ALL_JOINED':
      return getUserFilterPreset('joined').filter;
    case 'ORG_INACTIVE_LEARNERS':
      return getUserFilterPreset('inactive-30').filter;
    case 'ORG_ENROLLED_LEARNERS':
      return getUserFilterPreset('enrolled-in-30-days').filter;
    case 'ORG_RECENT_COMPLETERS':
      return getUserFilterPreset('1-completion-in-30-days').filter;
    case 'ORG_INCOMPLETE_IN_COURSE':
      return buildAssignedContentFilter({
        contentId: spec.courseId,
        contentType: 'COURSE',
        contentName: spec.courseName,
        isEnrolled: true,
        isCompleted: false,
      }).filter;
    default: {
      const exhaustive: never = spec;
      throw new Error(`Unhandled audience spec: ${JSON.stringify(exhaustive)}`);
    }
  }
};

/**
 * Legacy `preset` value for the existing send API's draft payload, derived from
 * the entry point's `audienceType` — deliberately NOT from the generate API's
 * `preset` (a different vocabulary that can misclassify, e.g. it tags this join
 * reminder as `enrollment_reminder`). The existing API still expects the legacy
 * `PresetId` enum from `enterprise-admin-messages`; we keep it as a local string
 * here to avoid taking a cross-bundle dependency just for this interim mapping.
 *
 * TODO: once the BE accepts the new HTML-template payload, pass the new preset
 * returned by the generate API instead of this legacy mapping.
 */
export const getLegacyMessagePreset = (audienceType: AudienceFilterSpec['audienceType']): string => {
  switch (audienceType) {
    case 'INVITED_NOT_JOINED':
    case 'ORG_INVITED_NOT_JOINED':
      return 'remind-join';
    case 'JOINED_NOT_ENROLLED':
      return 'remind-enroll';
    case 'ORG_ALL_JOINED':
    case 'ORG_INACTIVE_LEARNERS':
      return 'remind-be-active-general-past-30-days';
    case 'ORG_ENROLLED_LEARNERS':
      return 'remind-complete-past-30-days';
    case 'ORG_RECENT_COMPLETERS':
      return 'congratulate-achievement';
    case 'ORG_INCOMPLETE_IN_COURSE':
      return 'remind-assigned-learners';
    default:
      throw new Error(`Unhandled audienceType: ${audienceType}`);
  }
};

/**
 * CTA button to attach to the sent message, by audience type.
 * Mirrors the CTA the legacy flow used for each preset so V2 reminders carry
 * the same button. Returned as the literal strings the draft API expects.
 */
export const getMessageCallToAction = (
  audienceType: AudienceFilterSpec['audienceType']
): 'JOIN_PROGRAM' | 'FIND_A_COURSE' | 'CONTINUE_LEARNING' => {
  switch (audienceType) {
    case 'INVITED_NOT_JOINED':
    case 'ORG_INVITED_NOT_JOINED':
      return 'JOIN_PROGRAM';
    case 'JOINED_NOT_ENROLLED':
    case 'ORG_INACTIVE_LEARNERS':
    case 'ORG_ALL_JOINED':
      return 'FIND_A_COURSE';
    case 'ORG_ENROLLED_LEARNERS':
    case 'ORG_RECENT_COMPLETERS':
    case 'ORG_INCOMPLETE_IN_COURSE':
      return 'CONTINUE_LEARNING';
    default: {
      const exhaustive: never = audienceType;
      throw new Error(`Unhandled audienceType: ${exhaustive}`);
    }
  }
};

/**
 * Creates a saved admin user filter (purpose `FILTER_PURPOSE_MESSAGE`) from a
 * client `UserFilter` and returns its `filterId` — the id the draft/publish step
 * sends, so the email reaches exactly the learners the filter resolves to.
 * `userIdentifiers` (mixed ids/emails) narrows the audience to specific learners
 * (a manual learners-table selection); omit it to target the whole filter.
 */
export const useCreateSavedFilter = () => {
  const createAdminUserFilter = useCreateAdminUserFilterMutation();

  return React.useCallback(
    async (orgId: string, clientFilter: UserFilter, userIdentifiers?: string[]): Promise<string | undefined> => {
      const filter = mapUserFilterFromClientToServerModel(
        clientFilter,
        userIdentifiers?.length ? { userIdentifiers } : undefined
      );
      const response = await createAdminUserFilter({
        variables: {
          input: {
            organizationId: orgId,
            filterName: '',
            filterPurpose: 'FILTER_PURPOSE_MESSAGE',
            filter,
          },
        },
      });
      return response.data?.CreateAdminUserFilterMutation.adminUserFilter.filterId;
    },
    [createAdminUserFilter]
  );
};

/**
 * Spec-based saved filter for the funnel entry points: builds the client filter
 * from the semantic `audienceType`, then creates the saved filter via
 * {@link useCreateSavedFilter}.
 */
export const useCreateMessageFilter = () => {
  const createSavedFilter = useCreateSavedFilter();

  return React.useCallback(
    (spec: AudienceFilterSpec): Promise<string | undefined> =>
      createSavedFilter(spec.orgId, buildClientUserFilter(spec)),
    [createSavedFilter]
  );
};

export default useCreateMessageFilter;
